/**
 * Write-off type field configuration for the Expense Inventory create form.
 * Resolves per-field visibility/required rules for a given write-off type,
 * from BU config (`getFormConfigurations().expenseInventory`) when
 * available, falling back to `DEFAULT_WRITE_OFF_CONFIG` otherwise.
 *
 * The backend returns `expenseInventory` as a flat `Form[]` (top-level
 * fields followed by one `FormSection` per write-off type, in the same
 * order as `WRITE_OFF_TYPES`) rather than a map keyed by write-off type —
 * `FormSection` has no `name` field in the GraphQL schema to select on, so
 * `parseExpenseInventoryFormConfig` below matches sections to write-off
 * types positionally.
 *
 * @module src/components/Inventory/expense/form/expenseInventoryConfig
 */
import { Form, FormField, FormSection } from '../../../Shared/FormEngine/FormEngine.types';

export interface ExpenseInventoryFieldRule {
  mappingKey: string;
  isShow: boolean;
  isRequired: boolean;
}

export type WriteOffType = 'Demo' | 'Lab' | 'Reposable';

export type ExpenseInventoryConfig = Partial<Record<WriteOffType, ExpenseInventoryFieldRule[]>>;

export interface ResolvedFieldConfig {
  [mappingKey: string]: { isShow: boolean; isRequired: boolean };
}

const WRITE_OFF_TYPES: WriteOffType[] = ['Demo', 'Lab', 'Reposable'];

const isFormSection = (item: FormField | FormSection): item is FormSection =>
  item.kind === 'section';

/**
 * Converts the raw `expenseInventory` `Form[]` payload from
 * `getFormConfigurations` into an `ExpenseInventoryConfig` map keyed by
 * write-off type, matching sections to `WRITE_OFF_TYPES` by position.
 */
export const parseExpenseInventoryFormConfig = (
  forms: Form | null | undefined
): ExpenseInventoryConfig => {
  if (!forms?.length) return {};

  const sections = forms.filter(isFormSection);
  const config: ExpenseInventoryConfig = {};

  WRITE_OFF_TYPES.forEach((writeOffType, index) => {
    const section = sections[index];
    if (!section) return;

    config[writeOffType] = section.fields.map(field => ({
      mappingKey: field.name,
      isShow: field.isShow ?? true,
      isRequired: field.required ?? false,
    }));
  });

  return config;
};

const FIELD_KEYS = [
  'writeOffType',
  'salesRep',
  'account',
  'shipTo',
  'usageDate',
  'usedInPatientCare',
  'marketingCollaboration',
  'doctorName',
  'facilityNameOrLocation',
];

const REQUIRED_BY_DEFAULT = ['writeOffType', 'salesRep', 'account', 'shipTo', 'usageDate'];

const buildDefaultRules = (): ExpenseInventoryFieldRule[] =>
  FIELD_KEYS.map(mappingKey => ({
    mappingKey,
    isShow: true,
    isRequired: REQUIRED_BY_DEFAULT.includes(mappingKey),
  }));

/**
 * Fallback field configuration used when no BU-specific configuration has
 * been returned by `getDetailsViewConfigurations`. Shows every field for
 * every write-off type; only Sales Rep, Account, and Usage Date
 * are required by default.
 */
export const DEFAULT_WRITE_OFF_CONFIG: Record<WriteOffType, ExpenseInventoryFieldRule[]> = {
  Demo: buildDefaultRules(),
  Lab: buildDefaultRules(),
  Reposable: buildDefaultRules(),
};

/** Resolves the field rules for a given write-off type into a lookup map keyed by mappingKey. */
export const getWriteOffFieldConfig = (
  config: ExpenseInventoryConfig | null | undefined,
  writeOffType: string | null | undefined
): ResolvedFieldConfig => {
  if (!writeOffType) return {};

  const rules =
    config?.[writeOffType as WriteOffType] ??
    DEFAULT_WRITE_OFF_CONFIG[writeOffType as WriteOffType] ??
    [];

  return rules.reduce((acc: ResolvedFieldConfig, rule) => {
    acc[rule.mappingKey] = { isShow: rule.isShow, isRequired: rule.isRequired };
    return acc;
  }, {});
};

/**
 * Whether a field should be shown, per the resolved field config. Fields with
 * no matching config entry (e.g. not part of the Expense Inventory - Create
 * config) default to visible.
 */
export const isFieldVisible = (fieldConfig: ResolvedFieldConfig, key: string): boolean =>
  fieldConfig[key]?.isShow !== false;

/** Whether a field is required, per the resolved field config. Defaults to false. */
export const isFieldRequired = (fieldConfig: ResolvedFieldConfig, key: string): boolean =>
  !!fieldConfig[key]?.isRequired;
